"use client";

import {
  isRouteErrorResponse,
  Links,
  Meta,
  Scripts,
  ScrollRestoration,
} from "react-router";

import type { Route } from "./+types/root";

import "./app.css";
import "./i18n";

import { LoadingScreen, ThemeProvider } from "~/components/ui";
import type { ScRoute } from "~/routes/nav-routes";

import { useEffect, useMemo, useState } from "react";
import { loadConfig } from "lib/config";
import useUserState from "./stores/user";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { navRoutes } from "./routes/nav-routes";
import type { UserInfo } from "./components/layouts/main-layout";
import MainLayout from "./components/layouts/main-layout";
import { Toaster } from "sonner";
import useUiThemeState from "./stores/ui/theme";
import { initMsal } from "./utils/msal";

import "leaflet/dist/leaflet.css";
import "react-leaflet-markercluster/styles";
import type { PublicClientApplication } from "@azure/msal-browser";

export const links: Route.LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
  },
];

export let msalInstance: PublicClientApplication | undefined;

export function HydrateFallback() {
  return <LoadingScreen />;
}

export function Layout({ children }: { children: React.ReactNode }) {
  const themeState = useUiThemeState();
  return (
    <html
      lang="en"
      className={themeState.mode}
      data-theme={`${themeState.theme}-${themeState.mode}`}
    >
      <head>
        <title>SensorOnline</title>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        <ThemeProvider defaultMode="dark">
          <QueryClientProvider client={queryClient}>
            {children}
            <Toaster position="top-center" />
          </QueryClientProvider>
        </ThemeProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

const queryClient = new QueryClient();
export default function App() {
  useEffect(() => {
    initMsal().then((d) => (msalInstance = d));
  }, []);

  const userState = useUserState();
  const [layoutUser, setLayoutUser] = useState<UserInfo | null>(null);
  const routes: ScRoute[] = useMemo(() => navRoutes, []);
  queryClient.setDefaultOptions({
    queries: {
      retry: 1,
      refetchOnWindowFocus: true,
      staleTime: 1000 * 60 * 1, // 5 minutes
    },
  });

  useEffect(() => {
    if (userState.user) {
      setLayoutUser({
        userId: userState.user.userId ?? 0,
        fullName: userState.user.fullName ?? "N/A",
        email: userState.user.email,
        companyName: userState.company?.name ?? "",
        companyId: userState.company?.companyId ?? 1,
      });
    }
  }, [userState.user, userState.company]);

  const [ready, setReady] = useState(false);
  useEffect(() => {
    loadConfig().then(() => setReady(true));
  }, []);

  if (!ready) return <LoadingScreen />;

  return <MainLayout routes={routes} user={layoutUser} />;
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = "Oops!";
  let details = "An unexpected error occurred.";
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Error";
    details =
      error.status === 404
        ? "The requested page could not be found."
        : error.statusText || details;
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  return (
    <main className="pt-16 p-4 container mx-auto">
      <h1>{message}</h1>
      <p>{details}</p>
      {stack && (
        <pre className="w-full p-4 overflow-x-auto">
          <code>{stack}</code>
        </pre>
      )}
    </main>
  );
}
